import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ErrorBoundary from "../../components/ErrorBoundary";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import { api } from "../../services/api";
import { formatBytes, formatUptime } from "../../utils/format";
import GoogleMeetCard from "./components/GoogleMeetCard";
import RecentActivityGrid from "./components/RecentActivityGrid";
import SystemHealthCard from "./components/SystemHealthCard";
import SystemSettingsCard from "./components/SystemSettingsCard";

function AdminSystemInner() {
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const debounceTimers = useRef({});

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    setLoading(true);
    try {
      const [healthRes, activityRes, settingsRes, googleStatusRes] =
        await Promise.all([
          api.get("/admin/system/health"),
          api.get("/admin/activity"),
          api.get("/admin/system/settings"),
          api.get("/admin/google/status"),
        ]);
      setHealth(healthRes.health);
      setActivity(activityRes.activity);
      setSettings(settingsRes.settings);
      setGoogleStatus(googleStatusRes.status);
    } catch (error) {
      toast.error("Failed to load system data");
    } finally {
      setLoading(false);
    }
  };



  const handleToggleSetting = (settingKey, value) => {
    // Optimistic UI update
    setSettings((prev) => ({
      ...prev,
      [settingKey]: value,
    }));
    setSavingKey(settingKey);

    // Debounce API call for this settingKey
    if (debounceTimers.current[settingKey]) {
      clearTimeout(debounceTimers.current[settingKey]);
    }
    debounceTimers.current[settingKey] = setTimeout(async () => {
      try {
        const payload = { [settingKey]: value };
        const response = await api.put("/admin/system/settings", payload);
        setSettings(response.settings);
        toast.success("Setting updated successfully");
      } catch (error) {
        // Revert on error
        setSettings((prev) => ({
          ...prev,
          [settingKey]: !value,
        }));
        toast.error(error.error || "Failed to update setting");
      } finally {
        setSavingKey(null);
        debounceTimers.current[settingKey] = null;
      }
    }, 400); // 400ms debounce
  };

  const handleTestGoogleMeet = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const response = await api.get("/admin/test-google-meet");
      setTestResult(response);
      if (response.success) {
        toast.success("Google Meet test passed");
      } else {
        toast.warn("Google Meet test returned fallback");
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: error.error || error.message || "Unknown error",
      });
      toast.error("Google Meet test failed");
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-4">
        <PageHeader title="System Management" />
        <button
          onClick={loadSystemData}
          disabled={loading}
          className={`ml-4 px-4 py-2 rounded bg-primary text-white font-medium shadow hover:bg-primary/90 transition disabled:opacity-60`}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      <SystemHealthCard health={health} formatUptime={formatUptime} formatBytes={formatBytes} />
      <GoogleMeetCard googleStatus={googleStatus} testResult={testResult} testLoading={testLoading} handleTestGoogleMeet={handleTestGoogleMeet} />
      <SystemSettingsCard settings={settings} savingKey={savingKey} handleToggleSetting={handleToggleSetting} />
      <RecentActivityGrid activity={activity} />
    </div>
  );
}

export default function AdminSystem() {
  return (
    <ErrorBoundary>
      <AdminSystemInner />
    </ErrorBoundary>
  );
}
